import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Button } from '#app/components/buttons/button.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Button',
}

const component: componentProps = {
	name: 'Button',
	shortName: 'Button',
	fileName: 'button',
	category: 'buttons',
	description: ['A button allows a user to perform an action with mouse, touch, or keyboard.'],
	features: [
		'Mouse, touch, keyboard, and focus interactions are normalized to ensure consistency across browsers and devices.',
		'Uses a native <button> element under the hood, with support for the Space and Enter keys.',
		'Hover, press, and keyboard focus states are provided for easy styling. These states only apply when interacting with an appropriate input device, unlike CSS pseudo classes.',
	],
	parts: [
		{
			name: 'Button',
			description: 'A button allows a user to perform an action with mouse, touch, or keyboard.',
			props: [
				{
					name: 'form',
					type: 'string',
					default: '-',
					description: 'The <form> element to associate the button with. The value of this attribute must be the id of a <form> in the same document.',
				},
				{
					name: 'formAction',
					type: 'string',
					default: '-',
					description: "The URL that processes the information submitted by the button. Overrides the action attribute of the button's form owner.",
				},
				{
					name: 'formEncType',
					type: 'string',
					default: '-',
					description: 'Indicates how to encode the form data that is submitted.',
				},
				{
					name: 'formMethod',
					type: 'string',
					default: '-',
					description: 'Indicates the HTTP method used to submit the form.',
				},
				{
					name: 'formNoValidate',
					type: 'boolean',
					default: '-',
					description: 'Indicates that the form is not to be validated when it is submitted.',
				},
				{
					name: 'formTarget',
					type: 'string',
					default: '-',
					description: "Overrides the target attribute of the button's form owner.",
				},
				{
					name: 'name',
					type: 'string',
					default: '-',
					description: "Submitted as a pair with the button's value as part of the form data.",
				},
				{
					name: 'value',
					type: 'string',
					default: '-',
					description: "The value associated with the button's name when it's submitted with the form data.",
				},
				{
					name: 'isDisabled',
					type: 'boolean',
					default: '-',
					description: 'Whether the button is disabled.',
				},
				{
					name: 'autoFocus',
					type: 'boolean',
					default: '-',
					description: 'Whether the element should receive focus on render.',
				},
				{
					name: 'type',
					type: "'button' | 'submit' | 'reset'",
					default: "'button'",
					description: 'The behavior of the button when used in an HTML form.',
				},
				{
					name: 'children',
					type: 'ReactNode | (values: ButtonRenderProps) => ReactNode',
					default: '-',
					description: 'The children of the component. A function may be provided to alter the children based on component state.',
				},
				{
					name: 'className',
					type: 'string | (values: ButtonRenderProps) => string',
					default: '-',
					description: 'The CSS className for the element. A function may be provided to compute the class based on component state.',
				},
				{
					name: 'style',
					type: 'CSSProperties | (values: ButtonRenderProps) => CSSProperties',
					default: '-',
					description: 'The inline style for the element. A function may be provided to compute the style based on component state.',
				},
			],
			events: [
				{
					name: 'onPress',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when the press is released over the target.',
				},
				{
					name: 'onPressStart',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when a press interaction starts.',
				},
				{
					name: 'onPressEnd',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when a press interaction ends, either over the target or when the pointer leaves the target.',
				},
				{
					name: 'onPressChange',
					type: '(isPressed: boolean) => void',
					description: 'Handler that is called when the press state changes.',
				},
				{
					name: 'onPressUp',
					type: '(e: PressEvent) => void',
					description: 'Handler that is called when a press is released over the target, regardless of whether it started on the target or not.',
				},
				{
					name: 'onFocus',
					type: '(e: FocusEvent<Target>) => void',
					description: 'Handler that is called when the element receives focus.',
				},
				{
					name: 'onBlur',
					type: '(e: FocusEvent<Target>) => void',
					description: 'Handler that is called when the element loses focus.',
				},
				{
					name: 'onFocusChange',
					type: '(isFocused: boolean) => void',
					description: "Handler that is called when the element's focus status changes.",
				},
				{
					name: 'onKeyDown',
					type: '(e: KeyboardEvent) => void',
					description: 'Handler that is called when a key is pressed.',
				},
				{
					name: 'onKeyUp',
					type: '(e: KeyboardEvent) => void',
					description: 'Handler that is called when a key is released.',
				},
				{
					name: 'onHoverStart',
					type: '(e: HoverEvent) => void',
					description: 'Handler that is called when a hover interaction starts.',
				},
				{
					name: 'onHoverEnd',
					type: '(e: HoverEvent) => void',
					description: 'Handler that is called when a hover interaction ends.',
				},
				{
					name: 'onHoverChange',
					type: '(isHovering: boolean) => void',
					description: 'Handler that is called when the hover state changes.',
				},
			],
			layout: [
				{
					name: 'slot',
					type: 'string | null',
					description:
						'A slot name for the component. Slots allow the component to receive props from a parent component. An explicit null value indicates that the local props completely override all props received from a parent.',
				},
			],
			state: [
				{
					renderProp: 'isHovered',
					CSSSelector: '[data-hovered]',
					description: 'Whether the button is currently hovered with a mouse.',
				},
				{
					renderProp: 'isPressed',
					CSSSelector: '[data-pressed]',
					description: 'Whether the button is currently in a pressed state.',
				},
				{
					renderProp: 'isFocused',
					CSSSelector: '[data-focused]',
					description: 'Whether the button is focused, either via a mouse or keyboard.',
				},
				{
					renderProp: 'isFocusVisible',
					CSSSelector: '[data-focus-visible]',
					description: 'Whether the button is keyboard focused.',
				},
				{
					renderProp: 'isDisabled',
					CSSSelector: '[data-disabled]',
					description: 'Whether the button is disabled.',
				},
			],
			accessibility: [
				{
					name: 'id',
					type: 'string',
					description: "The element's unique identifier.",
				},
				{
					name: 'excludeFromTabOrder',
					type: 'boolean',
					description:
						'Whether to exclude the element from the sequential tab order. If true, the element will not be focusable via the keyboard by tabbing. This should be avoided except in rare scenarios where an alternative means of accessing the element or its functionality via the keyboard is available.',
				},
				{
					name: 'aria-expanded',
					type: "boolean | 'true' | 'false'",
					description: 'Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed.',
				},
				{
					name: 'aria-haspopup',
					type: "boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | 'true' | 'false'",
					description: 'Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element.',
				},
				{
					name: 'aria-controls',
					type: 'string',
					description: 'Identifies the element (or elements) whose contents or presence are controlled by the current element.',
				},
				{
					name: 'aria-pressed',
					type: "boolean | 'true' | 'false' | 'mixed'",
					description: 'Indicates the current "pressed" state of toggle buttons.',
				},
				{
					name: 'aria-label',
					type: 'string',
					description: 'Defines a string value that labels the current element.',
				},
				{
					name: 'aria-labelledby',
					type: 'string',
					description: 'Identifies the element (or elements) that labels the current element.',
				},
				{
					name: 'aria-describedby',
					type: 'string',
					description: 'Identifies the element (or elements) that describes the object.',
				},
				{
					name: 'aria-details',
					type: 'string',
					description: 'Identifies the element (or elements) that provide a detailed, extended description for the object.',
				},
			],
		},
	],
	usage: `<div className="flex gap-6">
	<Button onPress={() => alert('Hello world!')}>Click me</Button>
	<Button onPress={() => alert('Hello world!')} variant="secondary">
		Click me
	</Button>
	<Button onPress={() => alert('Hello world!')} variant="danger">
		Click me
	</Button>
	<Button onPress={() => alert('Hello world!')} variant="ghost">
		Click me
	</Button>
</div>
<div className="mt-8 flex gap-6">
	<Button isDisabled onPress={() => alert('Hello world!')}>
		Click me
	</Button>
	<Button isDisabled onPress={() => alert('Hello world!')} variant="secondary">
		Click me
	</Button>
	<Button isDisabled onPress={() => alert('Hello world!')} variant="danger">
		Click me
	</Button>
	<Button isDisabled onPress={() => alert('Hello world!')} variant="ghost">
		Click me
	</Button>
</div>`,
	styling: `/* ### Buttons ### */
@layer components {
	/* Button */
	/* For cross-browser consistency, React Aria's button give us the button's state using data attributes. We'll be using that instead of hover:, focus-visible:, disabled: and other pseudo class selectors. */
	.button {
		@apply inline-flex items-center justify-center whitespace-nowrap rounded-md text-size-sm font-semibold ring-offset-background transition-colors;
		/* States */
		&[data-focused] {
			@apply outline-none;
		}
		&[data-focus-visible] {
			@apply outline-none ring-2 ring-ring ring-offset-2;
		}
		&[data-disabled] {
			@apply pointer-events-none opacity-50;
		}

		/* Variants */
		&.primary {
			@apply border bg-brand text-muted-50;
			&[data-hovered] {
				@apply bg-brand/90;
			}
			&[data-pressed] {
				@apply bg-brand-bg-hover;
			}
		}
		&.secondary {
			@apply border bg-muted-50 text-muted-800;
			&[data-hovered] {
				@apply bg-muted-100;
			}
			&[data-pressed] {
				@apply bg-muted-200;
			}
		}
		&.danger {
			@apply bg-danger-bg text-danger-foreground;
			&[data-hovered] {
				@apply bg-danger-foreground/10;
			}
			&[data-pressed] {
				@apply bg-danger-foreground/20;
			}
		}
		&.ghost {
			&[data-hovered] {
				@apply bg-muted-100;
			}
			&[data-pressed] {
				@apply bg-muted-300;
			}
		}
		/* Sizes */
		&.lg {
			@apply h-14 px-10 text-size-md;
		}
		&.md {
			@apply h-10 px-4 py-2;
		}
		&.sm {
			@apply h-9 px-3;
		}
	}
}`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const ButtonRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<Parts parts={parts} />
			<Usage usage={usage} shortName={shortName} />
			<div className="flex gap-6">
				<Button onPress={() => alert('Hello world!')}>Click me</Button>
				<Button onPress={() => alert('Hello world!')} variant="secondary">
					Click me
				</Button>
				<Button onPress={() => alert('Hello world!')} variant="danger">
					Click me
				</Button>
				<Button onPress={() => alert('Hello world!')} variant="ghost">
					Click me
				</Button>
			</div>
			<div className="mt-8 flex gap-6">
				<Button isDisabled onPress={() => alert('Hello world!')}>
					Click me
				</Button>
				<Button isDisabled onPress={() => alert('Hello world!')} variant="secondary">
					Click me
				</Button>
				<Button isDisabled onPress={() => alert('Hello world!')} variant="danger">
					Click me
				</Button>
				<Button isDisabled onPress={() => alert('Hello world!')} variant="ghost">
					Click me
				</Button>
			</div>
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default ButtonRoute
